// components/Dashboard.tsx
// Main dashboard with scheduling access

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchQuickStats } from '../services/analytics';
import { getMessagingQuickStats } from '../services/adminMessaging';
import './Dashboard.css';

export const Dashboard: React.FC = () => {
    const { currentUser, signOut } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalPosts: 0,
        totalViews: 0,
        totalCompliments: 0,
        queueSize: 0,
        pendingMessages: 0
    });

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const [barkBoardStats, messagingStats] = await Promise.all([
                fetchQuickStats(),
                getMessagingQuickStats().catch(() => ({
                    pendingMessages: 0,
                    totalMessages: 0,
                    totalResponses: 0,
                    avgResponseTimeMinutes: 0
                }))
            ]);

            setStats({
                ...barkBoardStats,
                pendingMessages: messagingStats.pendingMessages
            });
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <div className="header-content">
                    <h1>Omore Admin Dashboard</h1>
                    <div className="header-actions">
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="dashboard-main">
                <div className="welcome-section">
                    <h2>Welcome to Control Centre</h2>
                    <p>Create and publish educational content for the Omore community</p>
                </div>

                <div className="nav-section">
                    <h3>📚 Manage Content</h3>
                    <div className="nav-buttons">
                        <button
                            onClick={() => navigate('/content-library')}
                            className="nav-button"
                        >
                            📖 View Content Library
                        </button>
                        <button
                            onClick={() => navigate('/scheduled-posts')}
                            className="nav-button"
                        >
                            📅 Scheduled Posts
                        </button>
                        <button
                            onClick={() => navigate('/drafts')}
                            className="nav-button secondary"
                        >
                            📝 My Drafts
                        </button>
                        <button
                            onClick={() => navigate('/analytics')}
                            className="nav-button"
                        >
                            📊 Analytics Dashboard
                        </button>
                        <button
                            onClick={() => navigate('/moderation')}
                            className="nav-button"
                        >
                            🛡️ Moderation Queue
                        </button>
                        <button
                            onClick={() => navigate('/podcast-moderation')}
                            className="nav-button"
                        >
                            🎤 Podcast Moderation
                        </button>
                        <button
                            onClick={() => navigate('/quiz-creator')}
                            className="nav-button"
                        >
                            🎯 Quiz Creator Portal
                        </button>
                        <button
                            onClick={() => navigate('/admin-messaging')}
                            className="nav-button"
                        >
                            💬 Admin Messaging
                            {stats.pendingMessages > 0 && (
                                <span className="badge">{stats.pendingMessages}</span>
                            )}
                        </button>
                        <button
                            onClick={() => navigate('/ai')}
                            className="nav-button"
                        >
                            🤖 AI & Analytics
                        </button>
                        <button
                            onClick={() => navigate('/ai/usage')}
                            className="nav-button secondary"
                        >
                            📊 AI Usage Analytics
                        </button>
                        <button
                            onClick={() => navigate('/omore')}
                            className="nav-button"
                        >
                            💬 Ask Omore Analytics
                        </button>
                        <button
                            onClick={() => navigate('/omore/moderation')}
                            className="nav-button"
                        >
                            🛡️ Omore Moderation
                        </button>
                        <button
                            onClick={() => navigate('/omore/users')}
                            className="nav-button secondary"
                        >
                            👤 Omore User Flags
                        </button>
                        <button
                            onClick={() => navigate('/omore/sessions')}
                            className="nav-button secondary"
                        >
                            📋 Omore Sessions
                        </button>
                        <button
                            onClick={() => navigate('/omore/trends')}
                            className="nav-button secondary"
                        >
                            📈 Omore Trends
                        </button>
                        <button
                            onClick={() => navigate('/omore/cohorts')}
                            className="nav-button secondary"
                        >
                            👥 Omore Cohort Analysis
                        </button>
                        <button
                            onClick={() => navigate('/alerts/settings')}
                            className="nav-button"
                        >
                            🔔 Alert Settings
                        </button>
                        <button
                            onClick={() => navigate('/admin-roles')}
                            className="nav-button"
                        >
                            👥 Admin Role Management
                        </button>
                        <button
                            onClick={() => navigate('/users')}
                            className="nav-button"
                        >
                            🏫 User Directory
                        </button>
                        <div className="nav-divider" style={{ width: '100%', borderTop: '1px solid #e5e7eb', margin: '8px 0' }}></div>
                        <p style={{ width: '100%', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '4px 0' }}>
                            Biz.omore.app
                        </p>
                        <button
                            onClick={() => navigate('/biz-spotlight')}
                            className="nav-button"
                        >
                            ✨ Biz Spotlight
                        </button>
                        <button
                            onClick={() => navigate('/biz-plans')}
                            className="nav-button"
                        >
                            📋 Biz Business Plans
                        </button>
                        <button
                            onClick={() => navigate('/biz-messages')}
                            className="nav-button"
                        >
                            💼 Biz Messages
                        </button>
                    </div>
                </div>

                <div className="content-cards">
                    <div className="content-card">
                        <div className="card-icon video-icon">🎥</div>
                        <h3>Upload Video</h3>
                        <p>Share video tutorials and educational content</p>
                        <button
                            onClick={() => navigate('/upload-video')}
                            className="card-button video-button"
                        >
                            Create Video Post
                        </button>
                    </div>

                    <div className="content-card">
                        <div className="card-icon audio-icon">🎵</div>
                        <h3>Upload Audio</h3>
                        <p>Publish podcasts and audio lessons</p>
                        <button
                            onClick={() => navigate('/upload-audio')}
                            className="card-button audio-button"
                        >
                            Create Audio Post
                        </button>
                    </div>

                    <div className="content-card">
                        <div className="card-icon article-icon">📝</div>
                        <h3>Write Article</h3>
                        <p>Write tips and storytelling guides</p>
                        <button
                            onClick={() => navigate('/create-article')}
                            className="card-button article-button"
                        >
                            Create Article
                        </button>
                    </div>

                    <div className="content-card quiz-card">
                        <div className="card-icon quiz-icon">🎯</div>
                        <h3>Quiz Creator</h3>
                        <p>View and manage quizzes created by students</p>
                        <button
                            onClick={() => navigate('/quiz-creator')}
                            className="card-button quiz-button"
                        >
                            View Quiz Library
                        </button>
                    </div>
                </div>

                <div className="stats-section">
                    <h3>Quick Stats</h3>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-value">{stats.totalPosts}</div>
                            <div className="stat-label">Published Posts</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{stats.totalViews.toLocaleString()}</div>
                            <div className="stat-label">Total Views</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{stats.totalCompliments.toLocaleString()}</div>
                            <div className="stat-label">Compliments</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{stats.queueSize}</div>
                            <div className="stat-label">Moderation Queue</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{stats.pendingMessages}</div>
                            <div className="stat-label">Pending Messages</div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};
